/* ==========================================================================
   viblii — Account & Rewards Drawer
   Intercepts clicks on `.header__icon-account` and toggles the right-side
   drawer rendered by `sections/viblii-account-drawer.liquid`.

   Close triggers: close button, backdrop click, Escape key.
   Accessibility: focus trap inside panel, body scroll lock, restored focus
   on close. Falls back to default anchor navigation if the script fails
   to load — the existing account icon link is untouched.
   ========================================================================== */
(function () {
  'use strict';

  var DRAWER_ID = 'viblii-account-drawer';
  var OPEN_CLASS = 'is-open';
  var BODY_CLASS = 'viblii-account-drawer-open';
  var ICON_SELECTOR = '.header__icon-account';

  var drawer = null;
  var panel = null;
  var lastFocusedElement = null;
  var savedScrollY = 0;

  function getFocusableElements() {
    if (!panel) return [];
    var selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];
    return Array.prototype.slice.call(panel.querySelectorAll(selectors.join(',')))
      .filter(function (el) {
        return el.offsetParent !== null; // visible
      });
  }

  function openDrawer() {
    if (!drawer) return;
    lastFocusedElement = document.activeElement;
    savedScrollY = window.scrollY || window.pageYOffset || 0;

    drawer.classList.add(OPEN_CLASS);
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add(BODY_CLASS);

    // Focus first focusable element in the panel after the transition starts.
    window.setTimeout(function () {
      var focusables = getFocusableElements();
      if (focusables.length > 0) {
        focusables[0].focus();
      } else if (panel) {
        panel.focus();
      }
    }, 50);

    document.addEventListener('keydown', onKeydown);
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove(OPEN_CLASS);
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(BODY_CLASS);

    document.removeEventListener('keydown', onKeydown);

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      try { lastFocusedElement.focus(); } catch (e) { /* ignore */ }
    }

    // Scroll position is preserved by overflow:hidden alone; no reset needed.
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key === 'Tab' || event.keyCode === 9) {
      var focusables = getFocusableElements();
      if (focusables.length === 0) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function onAccountIconClick(event) {
    // Allow modifier-key clicks to behave normally (open in new tab, etc.)
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    openDrawer();
  }

  function bindAccountIcons() {
    var icons = document.querySelectorAll(ICON_SELECTOR);
    for (var i = 0; i < icons.length; i++) {
      var icon = icons[i];
      if (icon.dataset.vibliiDrawerBound === '1') continue;
      icon.dataset.vibliiDrawerBound = '1';
      icon.addEventListener('click', onAccountIconClick);
    }
  }

  function bindCloseTriggers() {
    if (!drawer) return;
    var closers = drawer.querySelectorAll('[data-drawer-close]');
    for (var i = 0; i < closers.length; i++) {
      closers[i].addEventListener('click', function (event) {
        event.preventDefault();
        closeDrawer();
      });
    }
  }

  function init() {
    drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;
    panel = drawer.querySelector('.viblii-account-drawer__panel');

    bindAccountIcons();
    bindCloseTriggers();

    // Glozin re-renders header fragments (e.g., on currency/locale switch).
    // Watch for new account icons and bind them on the fly.
    if (typeof MutationObserver === 'function') {
      var observer = new MutationObserver(function () {
        bindAccountIcons();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    // Expose minimal API for debugging / potential external triggers.
    window.vibliiAccountDrawer = {
      open: openDrawer,
      close: closeDrawer
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
